import styled, { css } from "styled-components";
import { FC, memo } from "react";
import { DateTime } from "luxon";

import { Td } from "@/components/Table";
import Text from "@/components/Text";
import ExtractorStatus from "./ExtractorStatus";
import { VideoFramesExportResource } from "@/types/resources";
import { VideoFramesExportType } from "@/utils/enums";

const Name = styled(Text)`
  margin-top: 4px;
`;

const FilesQuantity = styled(Text)`
  display: flex;
  align-items: center;
  svg {
    margin-right: 6px;
  }
`;

const DownloadButton = styled.a<{ $disabled: boolean }>`
  width: 24px;
  height: 24px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: color ease-out 0.2s;
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.gray : theme.colors.black};
  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
    `}
  &:hover {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const NewBadge = styled(Text)`
  background: #ecf9f1;
  border-radius: 2px;
  display: inline-block;
  margin-left: 6px;
  padding: 2px 6px;
  flex-shrink: 0;
`;

const DateHeading = styled(Text)`
  display: flex;
  align-items: flex-start;
`;

const renderTypeTitle = (type: VideoFramesExportType) => {
  switch (type) {
    case VideoFramesExportType.PART_VIDEO:
      return "Part of video";
    case VideoFramesExportType.FULL_VIDEO:
      return "Full video";
    default:
      return "-";
  }
};

interface Props {
  framesExport: VideoFramesExportResource;
}

const ExtractorRecentResultsRow: FC<Props> = memo(({ framesExport }) => {
  const dt = DateTime.fromISO(framesExport.createdAt);

  const diffInMinutes = dt.diffNow("minutes").minutes;

  const isNew = diffInMinutes > -90;

  return (
    <tr>
      <Td>
        <DateHeading $type="smallHeadline">
          {framesExport.name}
          {isNew && (
            <NewBadge as="span" $color="success" $type="extraSmall">
              New
            </NewBadge>
          )}
        </DateHeading>
        {!!framesExport.userName && (
          <Name $type="extraSmall" $color="gray">
            {framesExport.userName}
          </Name>
        )}
      </Td>
      <Td>
        <Text $type="smallHeadline">{dt.toFormat("LL/dd/yyyy")}</Text>
      </Td>
      <Td>
        <Text $type="smallHeadline">
          <ExtractorStatus status={framesExport.status} />
        </Text>
      </Td>
      <Td>
        <Text $type="smallHeadline">{renderTypeTitle(framesExport.type)}</Text>
      </Td>
      <Td>
        <Text $type="smallHeadline">{framesExport.fps}</Text>
      </Td>
      <Td>
        <FilesQuantity $type="smallHeadline">
          <svg
            width="14"
            height="12"
            viewBox="0 0 14 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.5 1H1.5C1.22386 1 1 1.22386 1 1.5V10.5C1 10.7761 1.22386 11 1.5 11H12.5C12.7761 11 13 10.7761 13 10.5V1.5C13 1.22386 12.7761 1 12.5 1Z"
              stroke="#909090"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M1 8.49998L4.14644 5.35354C4.19287 5.30711 4.24799 5.27028 4.30865 5.24515C4.36931 5.22003 4.43433 5.20709 4.49999 5.20709C4.56565 5.20709 4.63067 5.22003 4.69133 5.24515C4.752 5.27028 4.80711 5.30711 4.85354 5.35354L7.64644 8.14643C7.69287 8.19286 7.74799 8.22969 7.80865 8.25482C7.86931 8.27995 7.93433 8.29288 7.99999 8.29288C8.06565 8.29288 8.13067 8.27995 8.19133 8.25482C8.252 8.22969 8.30711 8.19286 8.35354 8.14643L9.64644 6.85354C9.69287 6.80711 9.74799 6.77028 9.80865 6.74515C9.86931 6.72003 9.93433 6.70709 9.99999 6.70709C10.0657 6.70709 10.1307 6.72003 10.1913 6.74515C10.252 6.77028 10.3071 6.80711 10.3535 6.85354L13 9.49999"
              stroke="#909090"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8.75 5C9.16421 5 9.5 4.66421 9.5 4.25C9.5 3.83579 9.16421 3.5 8.75 3.5C8.33579 3.5 8 3.83579 8 4.25C8 4.66421 8.33579 5 8.75 5Z"
              fill="#909090"
            />
          </svg>
          {framesExport.filesCount ?? "-"}
        </FilesQuantity>
      </Td>
      <Td>
        <DownloadButton
          href={framesExport.downloadUrl}
          $disabled={!framesExport.downloadUrl}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 6.78571L6 10.7857L10 6.78571"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 1.64285V10.7857"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </DownloadButton>
      </Td>
    </tr>
  );
});

export default ExtractorRecentResultsRow;
